import { useFetcher, useRevalidator } from '@remix-run/react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import type { loader } from '~/routes/_app._pm.app.project.$id.design-boards.$boardId';
import { useLoaderData } from '@remix-run/react';
import { useUserLoggedIn } from '~/modules/user/hook/use-user';

import { UPDATE_BUILD_HUB_INSPIRATION_ITEM_VOTING_API_ROUTE } from '~/routes/remix-api.app.build-hub-inspiration-item-voting';
import type {
    SaveReactionMutation,
    RemoveReactionMutation,
} from '../../../../generated/graphql';

import {
    ReactionTypeEnum,
    BuildHubRoleEnum,
} from '../../../../generated/graphql';
import type {
    GroupedReactions,
    ReactionVoter,
} from '../data-mappers/map-inspiration-reactions';
import { useBuildHubData } from './use-build-hub-data';
import { useBuildHubVoteUpdates } from '../context/BuildHubRealTimeContext';

interface UseBuildHubBoardItemVotingProps {
    targetId: number;
    onSuccess?: (data: SaveReactionMutation | RemoveReactionMutation) => void;
}

interface UseBuildHubBoardItemVotingResult {
    reaction: GroupedReactions | undefined;
    handleUpVote: () => Promise<void>;
    handleDownVote: () => Promise<void>;
    handleRevokeVote: (reaction: ReactionTypeEnum) => Promise<void>;
    isVoting: boolean;
    upVoteCollaborators: ReactionVoter[];
    downVoteCollaborators: ReactionVoter[];
    isVotedUp: boolean;
    isVotedDown: boolean;
}

export const useBuildHubBoardItemVoting = (
    props: UseBuildHubBoardItemVotingProps
): UseBuildHubBoardItemVotingResult => {
    const { targetId, onSuccess } = props;
    const { reactions } = useLoaderData<typeof loader>();
    const { buildHub } = useBuildHubData();

    const collaborators = buildHub.collaborators;

    const { user } = useUserLoggedIn();
    if (!user || user.__typename === 'Guest') {
        throw new Error('User not found');
    }

    const matchedReaction = reactions.find(
        (reaction) => reaction && reaction.id === Number(targetId)
    );

    const fetcher = useFetcher<SaveReactionMutation | RemoveReactionMutation>();
    const revalidator = useRevalidator();
    const onSuccessRef = useRef(onSuccess);

    const reaction = matchedReaction?.reactions;
    const serverUpVoteCollaborators = useMemo(
        () => reaction?.up_vote ?? [],
        [reaction?.up_vote]
    );
    const serverDownVoteCollaborators = useMemo(
        () => reaction?.down_vote ?? [],
        [reaction?.down_vote]
    );

    // Subscribe to BuildHub real-time vote updates for this document
    useBuildHubVoteUpdates(targetId, (message) => {
        // Only update if it's not from the current user (to avoid double updates)
        if (message.member_id !== user.ID) {
            // Revalidate loader data to get updated voter lists
            revalidator.revalidate();
        }
    });

    // Update the ref when onSuccess changes
    useEffect(() => {
        onSuccessRef.current = onSuccess;
    }, [onSuccess]);

    const handleUpVote = useCallback(async () => {
        // Submit immediately for instant optimistic UI
        // fetcher.formData will be available right away
        fetcher.submit(
            {
                targetId,
                reaction: ReactionTypeEnum.UpVote,
                intent: 'save',
            },
            {
                method: 'post',
                action: UPDATE_BUILD_HUB_INSPIRATION_ITEM_VOTING_API_ROUTE,
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, fetcher]);

    const handleDownVote = useCallback(async () => {
        // Submit immediately for instant optimistic UI
        // fetcher.formData will be available right away
        fetcher.submit(
            {
                targetId,
                reaction: ReactionTypeEnum.DownVote,
                intent: 'save',
            },
            {
                method: 'post',
                action: UPDATE_BUILD_HUB_INSPIRATION_ITEM_VOTING_API_ROUTE,
            }
        );
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [targetId, fetcher]);

    const handleRevokeVote = useCallback(
        async (reaction: ReactionTypeEnum) => {
            if (!user) return;

            // Submit immediately for instant optimistic UI
            // fetcher.formData will be available right away
            fetcher.submit(
                {
                    targetId,
                    reaction,
                    intent: 'remove',
                },
                {
                    method: 'post',
                    action: UPDATE_BUILD_HUB_INSPIRATION_ITEM_VOTING_API_ROUTE,
                }
            );
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [targetId, user, fetcher]
    );

    // Remix optimistic UI pattern: use fetcher.formData if submitting, otherwise use server data
    const {
        upVoteCollaborators,
        downVoteCollaborators,
        isVotedUp,
        isVotedDown,
    } = useMemo(() => {
        // Start with server data
        let upVoters = [...serverUpVoteCollaborators];
        let downVoters = [...serverDownVoteCollaborators];

        // Check if there's a pending optimistic action
        if (fetcher.formData) {
            const intent = fetcher.formData.get('intent');
            const reaction = fetcher.formData.get('reaction');

            const optimisticUserVoter = getUserVoter(
                user,
                collaborators,
                buildHub.ownerId,
                buildHub.owner
            );

            // Remove user from both lists first
            upVoters = upVoters.filter((v) => v.id !== user.ID);
            downVoters = downVoters.filter((v) => v.id !== user.ID);

            // Add user to the appropriate list based on intent
            if (intent === 'save') {
                if (reaction === ReactionTypeEnum.UpVote) {
                    upVoters.push(optimisticUserVoter);
                } else if (reaction === ReactionTypeEnum.DownVote) {
                    downVoters.push(optimisticUserVoter);
                }
            }
            // if intent === 'remove', user stays removed from both lists
        }

        const votedUp = upVoters.some((v) => v.id === user.ID);
        const votedDown = downVoters.some((v) => v.id === user.ID);

        return {
            upVoteCollaborators: upVoters,
            downVoteCollaborators: downVoters,
            isVotedUp: votedUp,
            isVotedDown: votedDown,
        };
    }, [
        fetcher.formData,
        serverUpVoteCollaborators,
        serverDownVoteCollaborators,
        user,
        collaborators,
        buildHub.ownerId,
        buildHub.owner,
    ]);

    // Handle fetcher success callback
    useEffect(() => {
        if (fetcher.state === 'idle' && fetcher.data) {
            if (onSuccessRef.current) {
                onSuccessRef.current(fetcher.data);
            }
        }
    }, [fetcher.state, fetcher.data]);

    return {
        reaction: matchedReaction?.reactions,
        upVoteCollaborators,
        downVoteCollaborators,
        isVotedUp,
        isVotedDown,
        handleUpVote,
        handleDownVote,
        handleRevokeVote,
        isVoting: fetcher.state !== 'idle',
    };
};

function getUserVoter(
    user: ReturnType<typeof useUserLoggedIn>['user'],
    collaborators: {
        id: string;
        name: string;
        email: string;
        role: BuildHubRoleEnum;
        image: string | null;
        memberID?: string;
    }[],
    ownerId: number,
    owner: {
        id: number;
        name: string;
        image: string | null;
    }
): ReactionVoter {
    if (user.__typename !== 'Me') {
        throw new Error('User not found');
    }

    // Check if the user is the owner first
    const isOwner = user.ID === ownerId;

    if (isOwner) {
        return {
            id: owner.id,
            name: owner.name,
            role: BuildHubRoleEnum.Admin,
            profileImages: owner.image,
            isOwner: true,
            lastVotedAt: new Date().toISOString(),
            email: user.Email,
        };
    }

    // Otherwise, find the user in the collaborators list
    const collaborator = collaborators?.find(
        (collaborator) => collaborator.email === user.Email
    );

    if (!collaborator) {
        throw new Error('Collaborator not found');
    }

    return {
        id: Number(collaborator.id),
        name: collaborator.name,
        role: collaborator.role,
        profileImages: collaborator.image,
        isOwner: false,
        lastVotedAt: new Date().toISOString(),
        email: collaborator.email,
    };
}
